<script lang="ts">
	//Image
	import EditIcon from "@hitit/hub/src/assets/Icons/Edit.png";
	import SaveIcon from "@hitit/hub/src/assets/Icons/Save.png";
	import type {
		ChangeableDisplayValue,
		StaticDisplayValue,
		MixinPlacedata,
		ImageFile,
		ImageData,
	} from "../../functions/place/types";
	// UI
	import MainImage from "../components/place/MainImage.svelte";
	import WelcomeMessage from "../components/place/WelcomeMessage.svelte";
	import StoreDescription from "../components/place/StoreDescription.svelte";
	import Hashtags from "../components/place/Hashtags.svelte";
	import BuisnessHour from "../components/place/BuisnessHour.svelte";
	import Menus from "../components/place/Menus.svelte";
	import Map from "../../components/place/Map.svelte";
	import ContainerLoading from "../components/common/ContainerLoading.svelte";
	import Address from "../components/place/Address.svelte";
	import Call from "../components/place/Call.svelte";
	import Category from "../components/place/Category.svelte";
	import SNS from "../components/place/SNS.svelte";
	import Album from "../components/place/Album.svelte";
	//Split
	import createPlaceVM from "../../functions/place/model";
	import type { PostPlaceDTO } from "@hitit/api";
	import type { SubscribedVM } from "../../functions/place/model";
	import { deepClone } from "@hitit/hub/src/utils/obj";
	import {
		PlaceGetConverter,
		PlacePostConverter,
	} from "../../functions/place/hellper";
	//Utils
	import { HubProfileBottomLineButton } from "@hitit/new_ui";
	import { t, api, notifications } from "@hitit/hub/src/stores";
	import { saveStatus, editStatus, popup } from "@hitit/hub/src/v7/store";
	import { putS3File } from "@hitit/api";
	import { onDestroy, onMount } from "svelte";
	import AreYouSure from "../components/common/AreYouSure.svelte";
	import { dataLayerPush } from "../utils";

	//
	type DisplayValue = ChangeableDisplayValue & StaticDisplayValue;
	//
	export let storeId = "";
	export let editable = true;
	//
	const unApprovedUserMessage =
		"승인되지 않은 스토어는 플레이스에 노출되지 않습니다. 관리자에게 문의해주세요";
	const saveFailedMessage = "플레이스 정보를 저장 도중 문제가 발생하였습니다.";
	const fileProsmise: (v: ImageData) => Promise<ImageData> = (v: ImageData) =>
		new Promise(async (resolve, reject) => {
			try {
				const clone: ImageData = deepClone(v);
				if (v.edit) {
					const id = await uploadFile(v.edit);
					clone.src = id;
					resolve(clone);
				} else {
					resolve(clone);
				}
			} catch (e) {
				reject(e);
			}
		});
	//
	let loading = false;
	let destroied = false;
	let originPlaceDTO: MixinPlacedata;
	let model: DisplayValue;
	let placeValueManager: SubscribedVM;
	let approved = false;

	async function uploadAWS(url: string, file: File, retryCount = 3) {
		try {
			await putS3File(url, file);
		} catch (e) {
			if (retryCount == 0) throw e;
			uploadAWS(url, file, retryCount - 1);
		}
	}

	async function uploadFile(file: ImageFile): Promise<string> {
		const fileUploadResponse = await $api.getFileUploadUrl({
			fileName: file.name.replace(/ /g, ""),
			fileType: file.type,
			objectPurpose: "STORE_PLACE_IMAGE",
		});
		const { url, id } = fileUploadResponse.data;

		await uploadAWS(url, file.file);

		return id;
	}

	async function actionHandler() {
		if (!$editStatus.place) {
			$editStatus.place = true;
			return;
		}

		try {
			if ($saveStatus.place) {
				return true;
			}
			$saveStatus.place = true;
			const origin = originPlaceDTO;
			const dynamicCopyValue = $placeValueManager.generatePropertyClone();
			const displayValue: DisplayValue = Object.assign(
				{},
				model,
				dynamicCopyValue
			);

			const albumImagePromises = displayValue.albumImages.map((v) =>
				fileProsmise(v)
			);
			const mainImagePromise = fileProsmise(displayValue.mainImage);
			const newAlbumImages: ImageData[] = await Promise.all([
				mainImagePromise,
				...albumImagePromises,
			]);

			const cloneModel: DisplayValue = displayValue;
			cloneModel.mainImage = newAlbumImages[0];
			cloneModel.albumImages = [
				...newAlbumImages.slice(1, newAlbumImages.length),
			];
			const postParams = PlacePostConverter.convertPlaceDataToPostParam(
				origin,
				cloneModel
			);

			const categories = postParams.categories;
			delete postParams.categories;
			const postPlaceParams = postParams as PostPlaceDTO;

			if (categories) {
				const param = categories.reduce((array, v) => {
					for (const item of v.storeItemDTOs) {
						array.push({
							id: item.id,
							placeEnabled: item.placeEnabled,
						});
					}
					return array;
				}, [] as { id: string; placeEnabled: boolean }[]);
				await $api.setStoreItemPlaceEnabled(storeId, param);
			}
			await $api.postPlaceInfomation(storeId, postPlaceParams);

			if ($placeValueManager) $placeValueManager.revokeAllImageURL();

			//Place regeneration Trigger
			fetch("HITIT_PLACE_URL" + "store/" + storeId).catch(() =>
				console.log("trigger 실패")
			);
			dataLayerPush("UPDATE_PLACE");
			getPlaceInfomation(storeId);
			$editStatus.place = false;
		} catch (error) {
			console.log(error);
			showError(saveFailedMessage);
		} finally {
			$saveStatus.place = false;
		}
	}
	function convertPlaceDTOToinitialProps(dto: MixinPlacedata): DisplayValue {
		const dynamicValues = PlaceGetConverter.convertChangeableDisplayValues(dto);
		const staticValues = PlaceGetConverter.convertFiexedDisplayValues(dto);

		return Object.assign({}, dynamicValues, staticValues);
	}

	function showError(message: string) {
		notifications.append("ERROR", message, "", undefined, "WARNING");
	}

	async function getPlaceInfomation(__storeId: string) {
		try {
			loading = true;
			const dataPromise = await Promise.all([
				$api.getStoreItems(__storeId),
				$api.getPlaceInfomation(__storeId),
			]);

			if (__storeId !== storeId) return;
			if (destroied) return;
			const categories = dataPromise[0].data.categoryDTOs;
			const placeDTO = dataPromise[1].data.placeDTO;
			const approvedStatus = placeDTO.storeApplicationStatus === "APPROVED";
			originPlaceDTO = Object.assign({}, placeDTO, { categories });
			model = convertPlaceDTOToinitialProps(originPlaceDTO);
			placeValueManager = createPlaceVM(model);
			approved = approvedStatus;
			!approvedStatus && showError(unApprovedUserMessage);

			return {
				placeValueManager,
				model,
			};
		} catch (e) {
			alert("플레이스 정보를 가져오는데 실패 하였습니다.");
			console.log(e);
			return e;
		} finally {
			$editStatus.place = false;
			loading = false;
		}
	}

	onDestroy(() => {
		destroied = true;
		if (!$saveStatus.place && $editStatus.place) {
			$popup = {
				component: AreYouSure,
				props: {
					accept: async () => {
						actionHandler();
						$popup = undefined;
					},
					cancel: () => ($popup = undefined),
					textTranslation: "global.Place_data_being_modified",
				},
			};
		}
	});
	onMount(() => {
		$popup = undefined;
	});
	$: getPlaceInfomation(storeId);
</script>

{#if $saveStatus.place}
	<ContainerLoading>
		<p id="save-status">{$t("global.saving_changes")}</p>
	</ContainerLoading>
{/if}
{#if loading}
	<ContainerLoading />
{/if}
<div class="container">
	<div class="header">
		<h3 class="title">{$t("hub.place.title_2")}</h3>
		{#if editable}
			<div class="actions" class:approved={approved}>
				{#if approved}
					<HubProfileBottomLineButton
						newTab
						rightIconVisible
						href={"HITIT_PLACE_URL" + "store/" + storeId}
					>
						<span class="place-anchor">{$t("global.go_direct")}</span>
					</HubProfileBottomLineButton>
				{/if}
				<div class="save-edit" on:click={actionHandler}>
					<span
						>{$editStatus.place ? $t("global.done") : $t("global.edit")}</span
					>
					<img
						src={$editStatus.place ? SaveIcon : EditIcon}
						alt={$editStatus.place ? "save" : "edit"}
					/>
				</div>
			</div>
		{/if}
	</div>
	<div class="preview">
		{#if placeValueManager}
			<div class="mainImageWrapper">
				<MainImage
					on:UPDATE_MAIN_IMAGE={(e) => {
						$placeValueManager.updateMainImage(e.detail);
					}}
					on:DELETE_MAIN_IMAGE={() => {
						$placeValueManager.deleteMainImage();
					}}
					editMode={$editStatus.place}
					src={$placeValueManager.getMainImage()}
				/>
			</div>
			<div class="chest">
				<WelcomeMessage
					storeId={storeId}
					editmode={$editStatus.place}
					storename={model.storeName}
				/>
				<StoreDescription
					editmode={$editStatus.place}
					on:UPDATE_STOREDESCRIPTION={(e) => {
						$placeValueManager.updateStoreDescription(e.detail.html);
					}}
					storedesc={$placeValueManager.getStoreDescription()}
				/>
				<Hashtags
					editmode={$editStatus.place}
					on:ADD_HASH_TAG={() => {
						$placeValueManager.addHashTag();
					}}
					on:DELETE_HASH_TAG={(e) => {
						$placeValueManager.deleteHashTag(e.detail);
					}}
					hashtags={$placeValueManager.getHashTags()}
				/>
			</div>
			<div class="aside-wrapper">
				<div class="aside left">
					<Address
						roadAddress={model.addressData.road}
						numbAddress={model.addressData.numb}
						editmode={$editStatus.place}
					/>
					<Call
						on:UPDATE_CALL_NUMBER={(e) => {
							$placeValueManager.updateCallNumber(e.detail);
						}}
						call={$placeValueManager.getCallNumber()}
						editmode={$editStatus.place}
					/>
					<Category
						category={model.storeCategory}
						editmode={$editStatus.place}
					/>
					<SNS
						on:SNS_UPDATE={(e) => {
							$placeValueManager.updateSNS(e.detail);
						}}
						insta={$placeValueManager.getSNS("insta")}
						youtube={$placeValueManager.getSNS("youtube")}
						naverBlog={$placeValueManager.getSNS("naver")}
						editmode={$editStatus.place}
					/>
					<BuisnessHour
						on:UPDATE_BREAKTIME={(e) => {
							$placeValueManager.updateBreakTime(e.detail);
						}}
						breakTime={$placeValueManager.getBreakTime()}
						editmode={$editStatus.place}
						buisnesshours={$placeValueManager.getOpenHours()}
					/>
					<Menus
						on:CHANGE_ALL_MENUS={(e) => {
							$placeValueManager.changeVisibleAllMenus(e.detail);
						}}
						on:CHANGE_MENU={(e) => {
							$placeValueManager.changeVisibleOneMenu(e.detail);
						}}
						editmode={$editStatus.place}
						categories={$placeValueManager.getCategories()}
					/>
				</div>
				<div class="aside right">
					<Map
						editMode={$editStatus.place}
						position={model.addressData.position}
					/>
					<Album
						editmode={$editStatus.place}
						images={$placeValueManager.getAlbumImages()}
						on:NEW_IMAGE={(e) => {
							$placeValueManager.addAlbumImage(e.detail);
						}}
						on:DELETE_IMAGE={(e) => {
							$placeValueManager.deleteAlbumImage(e.detail);
						}}
						on:UPDATE_IMAGE={(e) => {
							$placeValueManager.updateAlbumImage(e.detail);
						}}
					/>
				</div>
			</div>
		{/if}
	</div>
</div>

<style lang="scss">
	@import "../../variables.scss";
	#save-status {
		font-size: 1.25rem;
		color: #46474b;
	}
	.container {
		display: flex;
		flex-direction: column;
		gap: 26px;

		.header {
			display: flex;
			align-items: center;
			gap: 30px;
			.title {
				display: none;
				color: #46474b;
				font-size: 2.1875rem;
				font-weight: 600;
			}
			.actions {
				padding-left: 60px;
				flex-grow: 1;
				display: flex;
				align-items: center;
				justify-content: flex-end;
				&.approved {
					justify-content: space-between;
				}

				.place-anchor {
					color: #3c68d6;
				}

				.save-edit {
					display: flex;
					gap: 10px;
					align-items: center;
					color: #6d6f76;
					font-weight: 500;

					&:hover {
						cursor: pointer;
					}
					img {
						width: 24px;
						height: 24px;
						object-fit: contain;
					}
				}
			}
		}
	}
	.preview {
		width: 100%;
		max-width: 1593px;
		background-color: white;
		display: flex;
		flex-direction: column;
		padding-bottom: 60px;

		.mainImageWrapper {
			width: 100%;
			height: 222px;
			background-color: #e6e7ed;
		}

		.chest {
			width: 100%;
		}

		.aside-wrapper {
			display: flex;
			flex-direction: column;
			box-sizing: border-box;
			padding: 0px 10px;
			width: 100%;
			margin-top: 30px;
			gap: 20px;

			.aside {
				display: flex;
				flex-direction: column;
				gap: 20px;
			}
		}
	}

	@include DesktopSize {
		.container {
			padding-top: 30px;
			gap: 30px;

			.header {
				.title {
					display: block;
				}
				.actions {
					padding-left: 0;
					.place-anchor {
						font-size: 1.25rem;
					}
					.save-edit {
						font-size: 1.25rem;
					}
				}
			}
		}

		.preview {
			.mainImageWrapper {
				height: 550px;
			}

			.aside-wrapper {
				flex-direction: row;
				gap: 0;
				padding: 0 60px;
				.aside {
					gap: 30px;

					&.left {
						/*  */
						flex-grow: 974;
						flex-basis: calc(100% * (974 / 1474));
					}
					&.right {
						gap: 10px;
						flex-grow: 500;
						min-width: 200px;
						flex-basis: calc(100% * (500 / 1474));
						max-width: 500px;
					}
				}
			}
		}
	}
</style>
